import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import Modal from '../../components/ui/Modal';
import { fmtDate } from '../../lib/constants';
import toast from 'react-hot-toast';

const DISTRICTS = ['Upendo','Wema','Furaha','Fadhili','Amani','Uaminifu'];

const TABS = [
  { key:'baptisms',      label:'💧 Baptisms',       color:'#0288D1' },
  { key:'confirmations', label:'✝️ Confirmations',   color:'#003399' },
  { key:'weddings',      label:'💍 Weddings',        color:'#AD1457' },
  { key:'burials',       label:'⚰️ Burials',         color:'#546E7A' },
];

const THIS_YEAR = new Date().getFullYear();
const YEARS     = Array.from({ length: 8 }, (_, i) => THIS_YEAR - i);

// ── How each tab maps to the member flag / date field ─────────
const MEMBER_FLAG_MAP = {
  baptisms:      { flag:'is_baptised',  dateField:'baptism_date'      },
  confirmations: { flag:'is_confirmed', dateField:'confirmation_date' },
  weddings:      { flag:'is_wedded',    dateField:'wedding_date'      },
  burials:       { flag:'is_deceased',  dateField:'death_date'        },
};

export default function TrackingPage() {
  const [tab,       setTab]       = useState('baptisms');
  const [year,      setYear]      = useState(THIS_YEAR);
  const [records,   setRecords]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [showAdd,   setShowAdd]   = useState(false);
  const [editRec,   setEditRec]   = useState(null);
  const [search,    setSearch]    = useState('');
  const [districtF, setDistrictF] = useState('');
  const [sourceF,   setSourceF]   = useState('all'); // all | members | manual
  const [saving,    setSaving]    = useState(false);

  const EMPTY = {
    baptisms:      { date:'', full_name:'', district:'', dob:'', parent_names:'', officiant:'', notes:'' },
    confirmations: { date:'', full_name:'', district:'', officiant:'', notes:'' },
    weddings:      { date:'', groom_name:'', bride_name:'', district:'', officiant:'', venue:'', notes:'' },
    burials:       { date:'', full_name:'', district:'', age:'', cause_of_death:'', officiant:'', notes:'' },
  };
  const [form, setForm] = useState({ ...EMPTY.baptisms });

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    const yStart = `${year}-01-01`;
    const yEnd   = `${year}-12-31`;
    const { flag, dateField } = MEMBER_FLAG_MAP[tab];

    // 1 ── Records from the dedicated table (manually added)
    const { data: manualData } = await supabase
      .from(tab)
      .select('*')
      .gte('date', yStart)
      .lte('date', yEnd)
      .order('date', { ascending: false });

    const manual = (manualData || []).map(r => ({ ...r, _source: 'manual' }));

    // 2 ── Records from member flags
    let memberRows = [];

    if (tab === 'baptisms') {
      const { data } = await supabase
        .from('members')
        .select('id,first_name,last_name,baptism_date,districts(name),dob,spouse_name,is_child,member_number')
        .eq('is_baptised', true)
        .not('baptism_date', 'is', null)
        .gte('baptism_date', yStart)
        .lte('baptism_date', yEnd);

      memberRows = (data || []).map(m => ({
        id:           `member:${m.id}`,
        _source:      'member',
        _member_id:   m.id,
        _member_no:   m.member_number,
        full_name:    `${m.first_name} ${m.last_name}`,
        date:         m.baptism_date,
        district:     m.districts?.name || '',
        dob:          m.dob || '',
        parent_names: m.spouse_name || '',
        officiant:    '',
        notes:        m.is_child ? 'Baptised child' : '',
      }));
    }

    if (tab === 'confirmations') {
      const { data } = await supabase
        .from('members')
        .select('id,first_name,last_name,confirmation_date,districts(name),member_number')
        .eq('is_confirmed', true)
        .not('confirmation_date', 'is', null)
        .gte('confirmation_date', yStart)
        .lte('confirmation_date', yEnd);

      memberRows = (data || []).map(m => ({
        id:         `member:${m.id}`,
        _source:    'member',
        _member_id: m.id,
        _member_no: m.member_number,
        full_name:  `${m.first_name} ${m.last_name}`,
        date:       m.confirmation_date,
        district:   m.districts?.name || '',
        officiant:  '',
        notes:      '',
      }));
    }

    if (tab === 'weddings') {
      const { data } = await supabase
        .from('members')
        .select('id,first_name,last_name,wedding_date,districts(name),gender,spouse_name,member_number')
        .eq('is_wedded', true)
        .not('wedding_date', 'is', null)
        .gte('wedding_date', yStart)
        .lte('wedding_date', yEnd);

      memberRows = (data || []).map(m => ({
        id:         `member:${m.id}`,
        _source:    'member',
        _member_id: m.id,
        _member_no: m.member_number,
        groom_name: m.gender === 'Male'   ? `${m.first_name} ${m.last_name}` : (m.spouse_name || '—'),
        bride_name: m.gender === 'Female' ? `${m.first_name} ${m.last_name}` : (m.spouse_name || '—'),
        date:       m.wedding_date,
        district:   m.districts?.name || '',
        officiant:  '',
        venue:      '',
        notes:      '(from member record)',
      }));
    }

    if (tab === 'burials') {
      const { data } = await supabase
        .from('members')
        .select('id,first_name,last_name,death_date,districts(name),dob,member_number')
        .eq('is_deceased', true)
        .not('death_date', 'is', null)
        .gte('death_date', yStart)
        .lte('death_date', yEnd);

      memberRows = (data || []).map(m => {
        let age = '';
        if (m.dob && m.death_date) {
          const diff = new Date(m.death_date) - new Date(m.dob);
          age = Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
        }
        return {
          id:             `member:${m.id}`,
          _source:        'member',
          _member_id:     m.id,
          _member_no:     m.member_number,
          full_name:      `${m.first_name} ${m.last_name}`,
          date:           m.death_date,
          district:       m.districts?.name || '',
          age,
          cause_of_death: '',
          officiant:      '',
          notes:          '(from member record)',
        };
      });
    }

    // 3 ── Merge: if a member already has a manual record for same event, skip the member row
    //     We identify duplicates by _member_id stored in manual records
    const manualMemberIds = new Set(
      manual.filter(r => r._member_id).map(r => r._member_id)
    );
    const dedupedMemberRows = memberRows.filter(r => !manualMemberIds.has(r._member_id));

    // 4 ── Combine and sort by date descending
    const merged = [...manual, ...dedupedMemberRows]
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

    setRecords(merged);
    setLoading(false);
  }, [tab, year]);

  useEffect(() => {
    setForm({ ...EMPTY[tab] });
    setSearch(''); setDistrictF(''); setSourceF('all');
    fetchRecords();
  }, [tab, year]);

  async function handleSave(e) {
    e.preventDefault(); setSaving(true);
    const payload = { ...form };
    if (!payload.date) payload.date = null;
    if (!payload.dob)  payload.dob  = null;
    if (payload.age === '') payload.age = null;

    if (editRec && editRec._source !== 'member') {
      const { error } = await supabase.from(tab).update(payload).eq('id', editRec.id);
      if (error) toast.error(error.message);
      else { toast.success('Updated ✅'); setEditRec(null); setShowAdd(false); }
    } else {
      const { error } = await supabase.from(tab).insert(payload);
      if (error) toast.error(error.message);
      else { toast.success('Record added ✅'); setShowAdd(false); }
    }
    setForm({ ...EMPTY[tab] });
    fetchRecords();
    setSaving(false);
  }

  async function handleDelete(rec) {
    if (rec._source === 'member') {
      toast('This record comes from the member profile. Go to Members → Edit Member to remove it.', { icon: 'ℹ️', duration: 4000 });
      return;
    }
    if (!window.confirm('Delete this record? This cannot be undone.')) return;
    const { error } = await supabase.from(tab).delete().eq('id', rec.id);
    if (error) toast.error(error.message);
    else { toast.success('Deleted'); fetchRecords(); }
  }

  function openEdit(rec) {
    if (rec._source === 'member') {
      toast('Edit this record by going to Members → Edit Member, then update the life event checkbox.', { icon: 'ℹ️', duration: 5000 });
      return;
    }
    setForm({ ...rec });
    setEditRec(rec);
    setShowAdd(true);
  }

  // ── Filters ──────────────────────────────────────────────────
  const filtered = records.filter(r => {
    const name = tab === 'weddings'
      ? `${r.groom_name || ''} ${r.bride_name || ''}`
      : (r.full_name || '');
    const matchName     = !search    || name.toLowerCase().includes(search.toLowerCase()) || (r.parent_names || '').toLowerCase().includes(search.toLowerCase());
    const matchDistrict = !districtF || r.district === districtF;
    const matchSource   = sourceF === 'all' || r._source === sourceF;
    return matchName && matchDistrict && matchSource;
  });

  // ── Stats ─────────────────────────────────────────────────────
  const fromMemberCount = records.filter(r => r._source === 'member').length;
  const manualCount     = records.filter(r => r._source === 'manual').length;
  const byDistrict      = {};
  records.forEach(r => { if (r.district) byDistrict[r.district] = (byDistrict[r.district] || 0) + 1; });
  const topDistrict = Object.entries(byDistrict).sort((a, b) => b[1] - a[1])[0];
  const tabInfo = TABS.find(t => t.key === tab);

  function addLabel() {
    return { baptisms:'Baptism', confirmations:'Confirmation', weddings:'Wedding', burials:'Burial' }[tab] || 'Record';
  }

  // ── Source badge ──────────────────────────────────────────────
  const SrcBadge = ({ r }) => r._source === 'member'
    ? <span style={{ fontSize:10, padding:'2px 8px', borderRadius:100, background:'#E3F2FD', color:'#003399', fontWeight:700, whiteSpace:'nowrap' }}>👤 Member{r._member_no ? ` · ${r._member_no}` : ''}</span>
    : <span style={{ fontSize:10, padding:'2px 8px', borderRadius:100, background:'#F3E5F5', color:'#6A1B9A', fontWeight:700, whiteSpace:'nowrap' }}>✏️ Manual</span>;

  // ── Row actions ───────────────────────────────────────────────
  const Actions = ({ r }) => (
    <td>
      <div className="flex gap-1">
        <button className="btn btn-sm btn-blue"   onClick={() => openEdit(r)}   title={r._source === 'member' ? 'Edit via member profile' : 'Edit'}>
          {r._source === 'member' ? '👤' : 'Edit'}
        </button>
        {r._source !== 'member' && (
          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(r)}>🗑</button>
        )}
      </div>
    </td>
  );

  // ── Empty row ─────────────────────────────────────────────────
  const NoData = ({ cols }) => (
    <tr>
      <td colSpan={cols} style={{ textAlign:'center', padding:36, color:'var(--text3)' }}>
        <div style={{ fontSize:28, marginBottom:8 }}>{tabInfo.label.split(' ')[0]}</div>
        <div style={{ fontWeight:600, marginBottom:4 }}>No {tab} recorded for {year}</div>
        <div style={{ fontSize:12 }}>
          Records marked on member profiles appear here automatically.<br />
          Or click <strong>+ Add {addLabel()}</strong> to enter one manually.
        </div>
      </td>
    </tr>
  );

  // ── Table per tab ─────────────────────────────────────────────
  function renderTable() {
    if (tab === 'baptisms') return (
      <table>
        <thead><tr><th>Source</th><th>Full Name</th><th>Date</th><th>District</th><th>Date of Birth</th><th>Parent / Guardian</th><th>Officiant</th><th>Notes</th><th>Actions</th></tr></thead>
        <tbody>
          {!filtered.length && <NoData cols={9} />}
          {filtered.map(r => (
            <tr key={r.id} style={{ background: r._source === 'member' ? 'rgba(3,169,244,0.04)' : 'white' }}>
              <td><SrcBadge r={r} /></td>
              <td style={{ fontWeight:700 }}>{r.full_name}</td>
              <td style={{ whiteSpace:'nowrap' }}>{fmtDate(r.date)}</td>
              <td>{r.district || '—'}</td>
              <td style={{ fontSize:12 }}>{r.dob ? fmtDate(r.dob) : '—'}</td>
              <td style={{ fontSize:12 }}>{r.parent_names || '—'}</td>
              <td style={{ fontSize:12 }}>{r.officiant || '—'}</td>
              <td style={{ fontSize:12, color:'var(--text3)' }}>{r.notes || '—'}</td>
              <Actions r={r} />
            </tr>
          ))}
        </tbody>
      </table>
    );

    if (tab === 'confirmations') return (
      <table>
        <thead><tr><th>Source</th><th>Full Name</th><th>Date</th><th>District</th><th>Officiant</th><th>Notes</th><th>Actions</th></tr></thead>
        <tbody>
          {!filtered.length && <NoData cols={7} />}
          {filtered.map(r => (
            <tr key={r.id} style={{ background: r._source === 'member' ? 'rgba(3,169,244,0.04)' : 'white' }}>
              <td><SrcBadge r={r} /></td>
              <td style={{ fontWeight:700 }}>{r.full_name}</td>
              <td style={{ whiteSpace:'nowrap' }}>{fmtDate(r.date)}</td>
              <td>{r.district || '—'}</td>
              <td style={{ fontSize:12 }}>{r.officiant || '—'}</td>
              <td style={{ fontSize:12, color:'var(--text3)' }}>{r.notes || '—'}</td>
              <Actions r={r} />
            </tr>
          ))}
        </tbody>
      </table>
    );

    if (tab === 'weddings') return (
      <table>
        <thead><tr><th>Source</th><th>Groom</th><th>Bride</th><th>Date</th><th>District</th><th>Officiant</th><th>Venue</th><th>Actions</th></tr></thead>
        <tbody>
          {!filtered.length && <NoData cols={8} />}
          {filtered.map(r => (
            <tr key={r.id} style={{ background: r._source === 'member' ? 'rgba(3,169,244,0.04)' : 'white' }}>
              <td><SrcBadge r={r} /></td>
              <td style={{ fontWeight:700 }}>{r.groom_name}</td>
              <td style={{ fontWeight:700 }}>{r.bride_name}</td>
              <td style={{ whiteSpace:'nowrap' }}>{fmtDate(r.date)}</td>
              <td>{r.district || '—'}</td>
              <td style={{ fontSize:12 }}>{r.officiant || '—'}</td>
              <td style={{ fontSize:12 }}>{r.venue || '—'}</td>
              <Actions r={r} />
            </tr>
          ))}
        </tbody>
      </table>
    );

    if (tab === 'burials') return (
      <table>
        <thead><tr><th>Source</th><th>Full Name</th><th>Date</th><th>District</th><th>Age</th><th>Officiant</th><th>Cause</th><th>Notes</th><th>Actions</th></tr></thead>
        <tbody>
          {!filtered.length && <NoData cols={9} />}
          {filtered.map(r => (
            <tr key={r.id} style={{ background: r._source === 'member' ? 'rgba(3,169,244,0.04)' : 'white' }}>
              <td><SrcBadge r={r} /></td>
              <td style={{ fontWeight:700 }}>{r.full_name}</td>
              <td style={{ whiteSpace:'nowrap' }}>{fmtDate(r.date)}</td>
              <td>{r.district || '—'}</td>
              <td>{r.age || '—'}</td>
              <td style={{ fontSize:12 }}>{r.officiant || '—'}</td>
              <td style={{ fontSize:12, color:'var(--text3)' }}>{r.cause_of_death || '—'}</td>
              <td style={{ fontSize:12, color:'var(--text3)' }}>{r.notes || '—'}</td>
              <Actions r={r} />
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // ── Form per tab ──────────────────────────────────────────────
  const s = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const DistSelect = ({ k='district' }) => (
    <select className="form-select" value={form[k]||''} onChange={s(k)}>
      <option value="">Select district…</option>
      {DISTRICTS.map(d => <option key={d}>{d}</option>)}
    </select>
  );

  function renderForm() {
    if (tab === 'baptisms') return (
      <div className="grid-2">
        <div className="form-group"><label className="form-label">Full Name *</label><input className="form-input" value={form.full_name||''} onChange={s('full_name')} required /></div>
        <div className="form-group"><label className="form-label">Date of Baptism *</label><input className="form-input" type="date" value={form.date||''} onChange={s('date')} required /></div>
        <div className="form-group"><label className="form-label">District</label><DistSelect /></div>
        <div className="form-group"><label className="form-label">Date of Birth</label><input className="form-input" type="date" value={form.dob||''} onChange={s('dob')} /></div>
        <div className="form-group"><label className="form-label">Parent / Guardian Names</label><input className="form-input" value={form.parent_names||''} onChange={s('parent_names')} placeholder="e.g. John & Mary Kamau" /></div>
        <div className="form-group"><label className="form-label">Officiant / Pastor</label><input className="form-input" value={form.officiant||''} onChange={s('officiant')} placeholder="Rev. …" /></div>
        <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} value={form.notes||''} onChange={s('notes')} /></div>
      </div>
    );
    if (tab === 'confirmations') return (
      <div className="grid-2">
        <div className="form-group"><label className="form-label">Full Name *</label><input className="form-input" value={form.full_name||''} onChange={s('full_name')} required /></div>
        <div className="form-group"><label className="form-label">Date of Confirmation *</label><input className="form-input" type="date" value={form.date||''} onChange={s('date')} required /></div>
        <div className="form-group"><label className="form-label">District</label><DistSelect /></div>
        <div className="form-group"><label className="form-label">Officiant / Bishop</label><input className="form-input" value={form.officiant||''} onChange={s('officiant')} placeholder="Bishop …" /></div>
        <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} value={form.notes||''} onChange={s('notes')} /></div>
      </div>
    );
    if (tab === 'weddings') return (
      <div className="grid-2">
        <div className="form-group"><label className="form-label">Groom's Full Name *</label><input className="form-input" value={form.groom_name||''} onChange={s('groom_name')} required /></div>
        <div className="form-group"><label className="form-label">Bride's Full Name *</label><input className="form-input" value={form.bride_name||''} onChange={s('bride_name')} required /></div>
        <div className="form-group"><label className="form-label">Date of Wedding *</label><input className="form-input" type="date" value={form.date||''} onChange={s('date')} required /></div>
        <div className="form-group"><label className="form-label">District</label><DistSelect /></div>
        <div className="form-group"><label className="form-label">Officiant / Pastor</label><input className="form-input" value={form.officiant||''} onChange={s('officiant')} placeholder="Rev. …" /></div>
        <div className="form-group"><label className="form-label">Venue</label><input className="form-input" value={form.venue||''} onChange={s('venue')} placeholder="PCEA Thingati Main Sanctuary" /></div>
        <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} value={form.notes||''} onChange={s('notes')} /></div>
      </div>
    );
    if (tab === 'burials') return (
      <div className="grid-2">
        <div className="form-group"><label className="form-label">Full Name *</label><input className="form-input" value={form.full_name||''} onChange={s('full_name')} required /></div>
        <div className="form-group"><label className="form-label">Date of Burial *</label><input className="form-input" type="date" value={form.date||''} onChange={s('date')} required /></div>
        <div className="form-group"><label className="form-label">District</label><DistSelect /></div>
        <div className="form-group"><label className="form-label">Age at Death</label><input className="form-input" type="number" min="0" value={form.age||''} onChange={s('age')} /></div>
        <div className="form-group"><label className="form-label">Officiant / Pastor</label><input className="form-input" value={form.officiant||''} onChange={s('officiant')} placeholder="Rev. …" /></div>
        <div className="form-group"><label className="form-label">Cause of Death</label><input className="form-input" value={form.cause_of_death||''} onChange={s('cause_of_death')} /></div>
        <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} value={form.notes||''} onChange={s('notes')} /></div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div className="flex-between mb-4">
        <div>
          <h1 style={{ fontSize:20, fontWeight:800 }}>📖 Life Events Tracking</h1>
          <div style={{ fontSize:13, color:'var(--text3)', marginTop:2 }}>
            Automatically pulled from member profiles · add extra records manually
          </div>
        </div>
        <div className="flex gap-2">
          {/* ── Year selector ── */}
          <select className="form-select" style={{ width:'auto', fontWeight:700 }} value={year} onChange={e => setYear(Number(e.target.value))}>
            {YEARS.map(y => (
              <option key={y} value={y}>{y === THIS_YEAR ? `${y} (Current)` : y}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={() => { setForm({...EMPTY[tab]}); setEditRec(null); setShowAdd(true); }}>
            + Add {addLabel()}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {TABS.map(t => (
          <button key={t.key} className={`tab${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid-4 mb-4">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: tabInfo.color + '22', fontSize:22 }}>{tabInfo.label.split(' ')[0]}</div>
          <div className="stat-value">{records.length}</div>
          <div className="stat-label">Total in {year}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background:'#E3F2FD', fontSize:16 }}>👤</div>
          <div className="stat-value">{fromMemberCount}</div>
          <div className="stat-label">From Member Profiles</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background:'#F3E5F5', fontSize:16 }}>✏️</div>
          <div className="stat-value">{manualCount}</div>
          <div className="stat-label">Manual Entries</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue">📍</div>
          <div className="stat-value" style={{ fontSize:16 }}>{topDistrict ? topDistrict[0] : '—'}</div>
          <div className="stat-label">Top District{topDistrict ? ` (${topDistrict[1]})` : ''}</div>
        </div>
      </div>

      {/* How data flows info */}
      <div style={{ background:'var(--blue-light)', border:'1px solid #90CAF9', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:12, color:'var(--blue-dark)' }}>
        <strong>How records appear here:</strong>
        <span style={{ margin:'0 6px' }}>·</span>
        <span style={{ padding:'1px 8px', background:'#E3F2FD', borderRadius:100, fontWeight:700 }}>👤 Member</span> — when you tick the life event checkbox on a member's profile (Members → Edit Member)
        <span style={{ margin:'0 10px' }}>|</span>
        <span style={{ padding:'1px 8px', background:'#F3E5F5', borderRadius:100, fontWeight:700 }}>✏️ Manual</span> — records added here for non-members or historical data
      </div>

      {/* Source filter + search + district */}
      <div className="card mb-4">
        <div className="card-pad" style={{ paddingTop:10, paddingBottom:10 }}>
          <div className="flex gap-2 flex-wrap">
            <div className="search-bar" style={{ flex:'1 1 180px' }}>
              🔍
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder={tab === 'weddings' ? 'Search groom or bride…' : tab === 'baptisms' ? 'Search name or parent…' : 'Search by name…'} />
              {search && <button onClick={() => setSearch('')} style={{ background:'none', border:'none', color:'var(--text3)', cursor:'pointer', fontSize:16 }}>✕</button>}
            </div>
            <select className="form-select" style={{ width:'auto' }} value={districtF} onChange={e => setDistrictF(e.target.value)}>
              <option value="">All Districts</option>
              {DISTRICTS.map(d => <option key={d}>{d}</option>)}
            </select>
            {/* Source filter buttons */}
            <div className="flex gap-1">
              {[['all','All'],['member','👤 Members'],['manual','✏️ Manual']].map(([v,l]) => (
                <button key={v} onClick={() => setSourceF(v)}
                  className={`btn btn-sm ${sourceF === v ? 'btn-blue' : 'btn-ghost'}`}
                  style={{ border: sourceF !== v ? '1.5px solid var(--border)' : '', fontSize:12 }}>
                  {l}
                </button>
              ))}
            </div>
            <div style={{ fontSize:12, color:'var(--text3)', alignSelf:'center' }}>
              {filtered.length} record{filtered.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading
        ? <div style={{ textAlign:'center', padding:36, color:'var(--text3)' }}>Loading {year} records…</div>
        : <div className="table-wrap">{renderTable()}</div>
      }

      {/* District breakdown bar chart */}
      {Object.keys(byDistrict).length > 0 && (
        <div className="card mt-4">
          <div className="card-header"><h3>By District — {year}</h3><span style={{ fontSize:12, color:'var(--text3)' }}>{records.length} total</span></div>
          <div className="card-pad">
            <div className="bar-chart">
              {Object.entries(byDistrict).sort((a,b)=>b[1]-a[1]).map(([d,c]) => (
                <div key={d} className="bar-row">
                  <span className="bar-label">{d}</span>
                  <div className="bar-track">
                    <div className="bar-fill blue" style={{ width:`${Math.round((c / Math.max(...Object.values(byDistrict))) * 100)}%` }}>
                      <span className="bar-fill-val">{c}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showAdd && (
        <Modal
          title={editRec ? `Edit ${addLabel()} Record` : `Add ${addLabel()} Record`}
          onClose={() => { setShowAdd(false); setEditRec(null); setForm({...EMPTY[tab]}); }}
          large
          footer={<>
            <button className="btn btn-ghost" onClick={() => { setShowAdd(false); setEditRec(null); setForm({...EMPTY[tab]}); }}>Cancel</button>
            <button className="btn btn-primary" form="track-form" type="submit" disabled={saving}>
              {saving ? 'Saving…' : editRec ? 'Save Changes' : `Add ${addLabel()}`}
            </button>
          </>}
        >
          <div className="alert alert-info mb-4" style={{ fontSize:12 }}>
            💡 This form adds a <strong>manual record</strong> — good for non-members or historical data.
            For church members, tick the checkbox on their <strong>member profile</strong> instead — it will appear here automatically with a <span style={{ background:'#E3F2FD', color:'#003399', padding:'1px 7px', borderRadius:100, fontWeight:700 }}>👤 Member</span> badge.
          </div>
          <form id="track-form" onSubmit={handleSave}>
            {renderForm()}
          </form>
        </Modal>
      )}
    </div>
  );
}